<template>
    <!-- UApp is mandatory: toasts, tooltips and programmatic overlays do not work
         without it, and it renders the toaster itself — there is no <Toast> to mount. -->
    <UApp :locale="uiLocale" :toaster="{ position: 'top-right' }">
        <!-- The layout renders the page itself: `templates/shell/` Default.vue and Blank.vue
             each carry their own `<router-view />` and take no slot. Nothing is drawn until the
             first navigation has resolved, or the sign-in page would open with a flash of the
             signed-in shell (the route before it has no `layout`) — and, signed in, until the
             person is known: the menu and whether this page is theirs at all depend on it. -->
        <component :is="layout" v-if="drawn" />

        <!-- The task drawer, one for the whole console: any page opens it with `?task=`. -->
        <TaskDrawer v-if="drawn && appStore.isLoggedIn() && !isBlank" />

        <!-- Something is being saved. A hairline across the top rather than a curtain over
             the page: a Kanban drop must not black out the board it was dropped on. -->
        <div
            v-if="appStore.isLoaderActive()"
            class="fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden bg-primary/20"
            role="progressbar"
            :aria-label="$t('general.saving')"
        >
            <div class="h-full w-1/3 animate-pulse bg-primary" />
        </div>
    </UApp>
</template>

<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import TaskDrawer from '@/components/task/TaskDrawer.vue'
    import { INTL_LOCALE, Locale } from '@/enums/locale'
    import Blank   from '@/layouts/Blank.vue'
    import Default from '@/layouts/Default.vue'
    import { landingRoute, routeAllowed } from '@/libs/access'
    import { uiLocaleFor } from '@/libs/i18n/uiLocale'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { IMe } from '@/types/User'

    const route      = useRoute()
    const router     = useRouter()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()
    const { locale } = useI18n()

    // Keeps Nuxt UI's built-in component texts (the calendar, "no data") in the reader's
    // language (`libs/i18n/uiLocale.ts`).
    const uiLocale = computed(() => uiLocaleFor(locale.value))

    const routerReady = ref(false)
    const meFailed    = ref(false)

    // A failed first navigation still draws the shell rather than an empty window.
    void router.isReady().catch(() => undefined).finally(() => { routerReady.value = true })

    const layouts = { Default, Blank }
    const layout  = computed(() => layouts[route.meta.layout ?? 'Default'])
    const isBlank = computed(() => route.meta.layout === 'Blank')

    /*
     * WHEN THE PAGE MAY BE DRAWN. Signed out, or on the sign-in screen, at once. Signed in, once
     * GET /me has said who this is and the page turned out to be theirs: the menu is made of
     * their modules, and a page of a module they do not have would otherwise open, ask the API,
     * show a 403 and only then be sent to their landing. Should /me fail for any other reason
     * than an expired session, the console is drawn anyway rather than left blank.
     *
     * It latches: once drawn, the shell stays, and a later change of the person's modules only
     * moves them to another page.
     */
    const drawn = ref(false)

    watch(
        () => routerReady.value && (
            !appStore.isLoggedIn()
            || isBlank.value
            || meFailed.value
            || (!!appStore.userData && routeAllowed(route.meta))
        ),
        (ready) => { if (ready) drawn.value = true },
        { immediate: true },
    )

    /*
     * Who is signed in is asked of the API, once per session: the token is the API's to
     * interpret. A login answers it itself (`user` in the answer), so this only runs on a
     * reload with a stored token — and a 401 here is the fetch store's ordinary logout.
     */
    const loadMe = async () => {
        meFailed.value = false

        const { statusCode, data } = await fetchStore.fetch('/me').get().json<IMe>()

        if (statusCode.value === 200 && data.value?.id) appStore.setUserData(data.value)
        else if (appStore.isLoggedIn()) meFailed.value = true
    }

    watch(() => appStore.JWT, (jwt) => {
        if (jwt && !appStore.userData) void loadMe()
    }, { immediate: true })

    /*
     * The person's modules are in: a page that is not theirs — opened from an old link before
     * they were known, or left behind when an administrator changed their access — gives way
     * to their landing page. The router's guard makes the same check on every later navigation.
     */
    watch(() => appStore.userData, (me) => {
        if (me && !routeAllowed(route.meta)) void router.replace(landingRoute())
    })

    watch(locale, (value) => {
        document.documentElement.lang = INTL_LOCALE[value as Locale] ?? value
    }, { immediate: true })
</script>
